import * as React from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type {
  ApiKeyDto,
  ApiKeyListDto,
  CreatedApiKeyDto,
  ModeSettingsDto,
  TenantProfileDto,
  TenantSettingsDto,
  WebhookSecretDto,
  GoLiveChecklistDto,
} from '@lib/dto/tenant-settings.dto'
import type { TenantMode } from '@lib/enums/tenant-mode.enum'
import type {
  CreateApiKeyInput,
  RotateWebhookSecretInput,
  UpdateModeSettingsInput,
} from '@lib/input/tenant-settings.input'
import type { TenantProfileInput } from '@lib/input/tenant.input'
import type { Uuid } from '@lib/validators/params'
import { refreshTenantSession, useDashboardScope } from '@/dashboard/DashboardScope'
import { useRefreshOnForbidden } from '@/dashboard/useRefreshOnForbidden'
import { teamKeys, tenantSettingsKeys } from '@/hooks/queryKeys'
import { TenantSettingsService } from '@/services/TenantSettingsService'

/** The settings service bound to the current dashboard session (tenant user or staff view-as). */
function useScopedSettingsService(): TenantSettingsService {
  const { client } = useDashboardScope()
  return React.useMemo(() => new TenantSettingsService(client), [client])
}

export function useApiKeys(): UseQueryResult<ApiKeyListDto> {
  const { tenantId, cacheKey } = useDashboardScope()
  const service = useScopedSettingsService()
  return useQuery({ queryKey: tenantSettingsKeys.keys(tenantId, cacheKey), queryFn: () => service.listKeys(tenantId) })
}

/** The go-live checklist; keys and settings changes invalidate it with the rest of the scope. */
export function useGoLiveChecklist(options: { enabled?: boolean } = {}): UseQueryResult<GoLiveChecklistDto> {
  const { tenantId, cacheKey } = useDashboardScope()
  const service = useScopedSettingsService()
  return useQuery({
    queryKey: tenantSettingsKeys.goLive(tenantId, cacheKey),
    queryFn: () => service.goLiveChecklist(tenantId),
    enabled: options.enabled ?? true,
  })
}

export function useTenantSettings(): UseQueryResult<TenantSettingsDto> {
  const { tenantId, cacheKey } = useDashboardScope()
  const service = useScopedSettingsService()
  return useQuery({
    queryKey: tenantSettingsKeys.settings(tenantId, cacheKey),
    queryFn: () => service.getSettings(tenantId),
  })
}

export function useTenantProfile(): UseQueryResult<TenantProfileDto> {
  const { tenantId, cacheKey } = useDashboardScope()
  const service = useScopedSettingsService()
  return useQuery({
    queryKey: tenantSettingsKeys.profile(tenantId, cacheKey),
    queryFn: () => service.profile(tenantId),
  })
}

/**
 * Changes to keys, settings, and the profile. Each refreshes this tenant's
 * settings and activity when it settles. Secrets come back in the mutation
 * result only; they are never put in the query cache.
 */
export function useSettingsActions(): {
  createKey: UseMutationResult<CreatedApiKeyDto, Error, CreateApiKeyInput>
  revokeKey: UseMutationResult<ApiKeyDto, Error, Uuid>
  updateSettings: UseMutationResult<ModeSettingsDto, Error, { mode: TenantMode; input: UpdateModeSettingsInput }>
  revealSecret: UseMutationResult<WebhookSecretDto, Error, TenantMode>
  rotateSecret: UseMutationResult<WebhookSecretDto, Error, { mode: TenantMode; input: RotateWebhookSecretInput }>
  retirePreviousSecret: UseMutationResult<ModeSettingsDto, Error, TenantMode>
  updateProfile: UseMutationResult<TenantProfileDto, Error, TenantProfileInput>
} {
  const scope = useDashboardScope()
  const { tenantId, cacheKey } = scope
  const service = useScopedSettingsService()
  const queryClient = useQueryClient()
  const onError = useRefreshOnForbidden()
  const onSettled = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: tenantSettingsKeys.scope(tenantId, cacheKey) }),
      queryClient.invalidateQueries({ queryKey: teamKeys.activity(tenantId, cacheKey) }),
    ])
  }
  return {
    createKey: useMutation({ mutationFn: (input) => service.createKey(tenantId, input), onError, onSettled }),
    revokeKey: useMutation({ mutationFn: (keyId) => service.revokeKey(tenantId, keyId), onError, onSettled }),
    updateSettings: useMutation({
      mutationFn: ({ mode, input }) => service.updateSettings(tenantId, mode, input),
      onError,
      onSettled,
    }),
    revealSecret: useMutation({ mutationFn: (mode) => service.webhookSecret(tenantId, mode), onError }),
    rotateSecret: useMutation({
      mutationFn: ({ mode, input }) => service.rotateWebhookSecret(tenantId, mode, input),
      onError,
      onSettled,
    }),
    retirePreviousSecret: useMutation({
      mutationFn: (mode) => service.retirePreviousWebhookSecret(tenantId, mode),
      onError,
      onSettled,
    }),
    updateProfile: useMutation({
      mutationFn: (input) => service.updateProfile(tenantId, input),
      onError,
      // The organization's name also lives in the session (the switcher).
      onSettled: async () => {
        await Promise.all([onSettled(), refreshTenantSession(scope) ?? Promise.resolve()])
      },
    }),
  }
}
